import React, { useRef, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import { Enemy as EnemyType } from '../store/gameStore';
import { Text } from '@react-three/drei';
import { Group, Vector3 } from 'three';

interface EnemyProps {
  enemy: EnemyType;
}

const Enemy: React.FC<EnemyProps> = ({ enemy }) => {
  const enemyRef = useRef<Group>(null);
  const floatRef = useRef(0);
  const spinRef = useRef(0);
  const glowRef = useRef(0);
  
  // Cleanup function when enemy is removed
  useEffect(() => {
    return () => {
      // Reset animation refs when component unmounts
      floatRef.current = 0;
      spinRef.current = 0;
      glowRef.current = 0;
    };
  }, []);
  
  // Floating animation
  useFrame((_, delta) => {
    if (enemyRef.current) {
      // If frozen, slow down animations based on freezeMultiplier
      const animationSpeed = enemy.isFrozen ? delta * enemy.freezeMultiplier : delta;
      
      floatRef.current += animationSpeed * 2;
      spinRef.current += animationSpeed * 1.5;
      glowRef.current += animationSpeed * 3;
      
      // Floating up and down
      enemyRef.current.position.y = 1 + Math.sin(floatRef.current) * 0.2;
      
      // Spinning
      enemyRef.current.rotation.y = spinRef.current;
    }
  });
  
  // Health bar
  const healthPercent = enemy.health / enemy.maxHealth;
  
  // Get enemy color based on type
  const getEnemyColor = () => {
    switch (enemy.type) {
      case 'alien':
        return '#00ff00';
      case 'ufo':
        return '#88ccff';
      case 'boss':
        return '#aa00aa';
      default:
        return '#00ff00';
    }
  };
  
  // Render different enemy types
  const renderEnemy = () => {
    switch (enemy.type) {
      case 'alien':
        return (
          <>
            {/* Alien body */}
            <mesh position={[0, 0, 0]}>
              <sphereGeometry args={[0.5, 16, 16]} />
              <meshStandardMaterial color="#00ff00" emissive="#00ff00" emissiveIntensity={0.5} />
            </mesh>
            
            {/* Alien eyes */}
            <mesh position={[0.2, 0.2, 0.4]}>
              <sphereGeometry args={[0.15, 16, 16]} />
              <meshStandardMaterial color="#000000" />
            </mesh>
            <mesh position={[-0.2, 0.2, 0.4]}>
              <sphereGeometry args={[0.15, 16, 16]} />
              <meshStandardMaterial color="#000000" />
            </mesh>
            
            {/* Alien antenna */}
            <mesh position={[0, 0.6, 0]}>
              <cylinderGeometry args={[0.05, 0.05, 0.4, 8]} />
              <meshStandardMaterial color="#00aa00" />
            </mesh>
            <mesh position={[0, 0.8, 0]}>
              <sphereGeometry args={[0.1, 16, 16]} />
              <meshStandardMaterial color="#ff0000" emissive="#ff0000" emissiveIntensity={0.8} />
            </mesh>
          </>
        );
      case 'ufo':
        return (
          <>
            {/* UFO body */}
            <mesh position={[0, 0, 0]}>
              <cylinderGeometry args={[0.7, 0.5, 0.2, 16]} />
              <meshStandardMaterial color="#aaaaaa" metalness={0.8} roughness={0.2} emissive="#444444" emissiveIntensity={0.3} />
            </mesh>
            
            {/* UFO dome */}
            <mesh position={[0, 0.2, 0]}>
              <sphereGeometry args={[0.4, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
              <meshStandardMaterial color="#88ccff" transparent opacity={0.7} emissive="#88ccff" emissiveIntensity={0.5} />
            </mesh>
            
            {/* UFO lights */}
            {Array.from({ length: 8 }).map((_, i) => (
              <mesh 
                key={`ufo-light-${i}`} 
                position={[
                  0.6 * Math.cos(i * Math.PI / 4), 
                  0, 
                  0.6 * Math.sin(i * Math.PI / 4)
                ]}
              >
                <sphereGeometry args={[0.1, 16, 16]} />
                <meshStandardMaterial 
                  color={['#ff0000', '#00ff00', '#0000ff', '#ffff00'][i % 4]} 
                  emissive={['#ff0000', '#00ff00', '#0000ff', '#ffff00'][i % 4]} 
                  emissiveIntensity={0.8} 
                />
              </mesh>
            ))}
            
            {/* UFO beam */}
            <mesh position={[0, -0.3, 0]}>
              <cylinderGeometry args={[0.1, 0.3, 0.4, 16]} />
              <meshStandardMaterial color="#ffffff" transparent opacity={0.5} emissive="#ffffff" emissiveIntensity={0.5} />
            </mesh>
          </>
        );
      case 'boss':
        return (
          <>
            {/* Boss body */}
            <mesh position={[0, 0, 0]}>
              <sphereGeometry args={[0.8, 16, 16]} />
              <meshStandardMaterial color="#aa00aa" emissive="#aa00aa" emissiveIntensity={0.5} />
            </mesh>
            
            {/* Boss armor plates */}
            {Array.from({ length: 4 }).map((_, i) => (
              <mesh 
                key={`boss-armor-${i}`} 
                position={[
                  0.6 * Math.cos(i * Math.PI / 2), 
                  0, 
                  0.6 * Math.sin(i * Math.PI / 2)
                ]}
                rotation={[0, i * Math.PI / 2, 0]}
              >
                <boxGeometry args={[0.5, 0.8, 0.1]} />
                <meshStandardMaterial color="#550055" metalness={0.7} roughness={0.3} emissive="#550055" emissiveIntensity={0.3} />
              </mesh>
            ))}
            
            {/* Boss eye */}
            <mesh position={[0, 0.2, 0.6]}>
              <sphereGeometry args={[0.3, 16, 16]} />
              <meshStandardMaterial color="#ff0000" emissive="#ff0000" emissiveIntensity={0.9} />
            </mesh>
            
            {/* Boss spikes */}
            {Array.from({ length: 6 }).map((_, i) => (
              <mesh 
                key={`boss-spike-${i}`} 
                position={[
                  0.8 * Math.cos(i * Math.PI / 3), 
                  0.8, 
                  0.8 * Math.sin(i * Math.PI / 3)
                ]}
                rotation={[
                  Math.PI / 2 - Math.atan2(
                    0.8 * Math.sin(i * Math.PI / 3),
                    0.8
                  ),
                  0,
                  -i * Math.PI / 3
                ]}
              >
                <cylinderGeometry args={[0.05, 0.15, 0.5, 8]} />
                <meshStandardMaterial color="#aa00aa" emissive="#aa00aa" emissiveIntensity={0.3} />
              </mesh>
            ))}
          </>
        );
      default:
        return null;
    }
  };
  
  return (
    <group position={enemy.position}>
      {/* Vertical beam for visibility */}
      <mesh position={[0, 2, 0]}>
        <cylinderGeometry args={[0.05, 0.05, 4, 8]} />
        <meshBasicMaterial 
          color={getEnemyColor()} 
          transparent
          opacity={0.3}
        />
      </mesh>
      
      {/* Ground marker */}
      <mesh position={[0, 0.05, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.7, 16]} />
        <meshBasicMaterial 
          color={getEnemyColor()} 
          transparent
          opacity={0.3}
        />
      </mesh>
      
      {/* Enemy model */}
      <group ref={enemyRef}>
        {/* Glow effect */}
        <mesh position={[0, 0, 0]}>
          <sphereGeometry args={[0.9, 16, 16]} />
          <meshBasicMaterial 
            color={getEnemyColor()} 
            transparent 
            opacity={0.2 + Math.sin(glowRef.current) * 0.1} 
          />
        </mesh>
        
        {renderEnemy()}
        
        {/* Freeze effect when enemy is frozen */}
        {enemy.isFrozen && (
          <>
            {/* Ice crystal shell */}
            <mesh position={[0, 0, 0]}>
              <icosahedronGeometry args={[1.1, 1]} />
              <meshStandardMaterial 
                color="#80ffff" 
                transparent 
                opacity={0.4}
                emissive="#00ffff"
                emissiveIntensity={0.3}
              />
            </mesh>
            
            {/* Ice particles */}
            {Array.from({ length: 8 }).map((_, i) => {
              const angle = (i / 8) * Math.PI * 2;
              const radius = 1.2;
              const y = Math.sin(glowRef.current + i) * 0.2;
              
              return (
                <mesh 
                  key={`ice-${i}`} 
                  position={[
                    Math.cos(angle) * radius,
                    y,
                    Math.sin(angle) * radius
                  ]}
                  rotation={[Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI]}
                >
                  <octahedronGeometry args={[0.15, 0]} />
                  <meshStandardMaterial 
                    color="#80ffff" 
                    transparent 
                    opacity={0.7}
                    emissive="#00ffff"
                    emissiveIntensity={0.5}
                  />
                </mesh>
              );
            })}
            
            {/* Freeze text */}
            <Text
              position={[0, 1.5, 0]}
              fontSize={0.3}
              color="#00ffff"
              anchorX="center"
              anchorY="middle"
            >
              FROZEN
            </Text>
          </>
        )}
      </group>
      
      {/* Health bar */}
      <group position={[0, 2, 0]}>
        {/* Health bar background */}
        <mesh position={[0, 0, 0]}>
          <boxGeometry args={[1, 0.2, 0.1]} />
          <meshBasicMaterial color="#333333" />
        </mesh>
        
        {/* Health bar fill */}
        <mesh position={[(healthPercent - 1) / 2, 0, 0.05]}>
          <boxGeometry args={[healthPercent, 0.15, 0.1]} />
          <meshBasicMaterial 
            color={
              healthPercent > 0.6 ? '#00ff00' : 
              healthPercent > 0.3 ? '#ffff00' : 
              '#ff0000'
            } 
          />
        </mesh>
      </group>
      
      {/* Enemy type indicator */}
      <Text
        position={[0, 2.5, 0]}
        fontSize={0.3}
        color="#ffffff"
        anchorX="center"
        anchorY="middle"
      >
        {enemy.type.toUpperCase()}
      </Text>
    </group>
  );
};

export default Enemy; 